import React, { useState } from 'react';

const UnifiedSimpleConversionExplanation = ({
  conversionType = 'general',
  fromUnit = '',
  toUnit = '',
  inputValue = '',
  result = '',
  darkMode = false,
  introduction = null,
  fundamentalEquality = null,
  formula = null,
  derivationSteps = [],
  conversionSteps = [],
  realWorldExample = null,
  funFacts = null,
  showExample = true
}) => {
  const [showDerivation, setShowDerivation] = useState(false);

  // Helper function to determine relationship statement style
  const getRelationshipHeader = () => {
    switch(conversionType) {
      case 'temperature':
        return "Therefore, we can establish:";
      case 'length':
      case 'weight':
      case 'volume':
        return "Basic Conversion Factor:";
      default:
        return "Key Relationship:";
    }
  };

  return (
    <div className="mt-8 space-y-8">
      {/* Introduction Section - Enhanced Design */}
      {introduction && (
        <div className={`
          relative overflow-hidden
          p-7 rounded-2xl shadow-lg transition-all duration-300
          ${darkMode 
            ? 'bg-gradient-to-br from-gray-800/90 via-gray-800/50 to-gray-800/90 border border-gray-700/50' 
            : 'bg-gradient-to-br from-white/90 via-white/50 to-white/90 border border-gray-200/50'}
        `}>
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-500/10 to-purple-500/10 blur-3xl -z-10"></div>
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-br from-purple-500/10 to-blue-500/10 blur-3xl -z-10"></div>
          
          <div className="prose dark:prose-invert max-w-none">
            {introduction.map((paragraph, index) => (
              <p key={index} 
                 className="mb-4 last:mb-0 text-gray-600 dark:text-gray-300 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Fundamental Equality - Enhanced Design */}
      {fundamentalEquality && (
        <div className="text-center p-6">
          <div className="relative inline-block group">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 
                           rounded-xl blur-lg group-hover:opacity-100 transition duration-300 opacity-75"></div>
            <div className="relative bg-gradient-to-br from-white via-white/95 to-white/90
                           dark:from-gray-800 dark:via-gray-800/95 dark:to-gray-800/90
                           rounded-xl p-8 shadow-xl border border-gray-200/50 dark:border-gray-700/50
                           transform transition-all duration-300 hover:scale-[1.02]">
              <h3 className="text-lg font-medium mb-4 bg-gradient-to-r from-blue-600 to-purple-600 
                           bg-clip-text text-transparent">
                {getRelationshipHeader()}
              </h3>
              <div className="text-2xl font-bold font-mono bg-gradient-to-br from-gray-50 to-white
                             dark:from-gray-900 dark:to-gray-800
                             p-6 rounded-lg shadow-inner border border-gray-200/50 dark:border-gray-700/50">
                {fundamentalEquality.statement}
              </div>
              {fundamentalEquality.explanation && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-4 italic leading-relaxed">
                  {fundamentalEquality.explanation}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Formula Section - Enhanced Design */}
      {formula && (
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 
                         rounded-xl blur-xl transform scale-105"></div>
          <div className={`relative p-8 rounded-xl shadow-lg transition-all duration-300
            ${darkMode 
              ? 'bg-gradient-to-br from-gray-800/90 via-gray-800/70 to-gray-800/90 border border-gray-700/50' 
              : 'bg-gradient-to-br from-white/90 via-white/70 to-white/90 border border-gray-200/50'}`}>
            <div className="space-y-6">
              <h3 className="text-xl font-semibold bg-gradient-to-r from-blue-600 to-purple-600 
                           bg-clip-text text-transparent">
                {conversionType === 'temperature' 
                  ? "Derived Conversion Formula"
                  : "Conversion Formula"}
              </h3>
              
              <div className="relative group">
                <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 
                               rounded-lg blur-sm opacity-75 group-hover:opacity-100 transition duration-300"></div>
                <div className="relative text-2xl font-mono text-center bg-gradient-to-br from-gray-50 to-gray-100/90
                               dark:from-gray-900 dark:to-gray-800/90 
                               p-6 rounded-lg shadow-inner border border-gray-200/50 dark:border-gray-700/50">
                  {typeof formula === 'object' ? formula.main : formula}
                </div>
              </div>

              {typeof formula === 'object' && formula.alternate && (
                <div className="text-sm font-mono text-center text-gray-600 dark:text-gray-400 mt-2
                                bg-gray-50 dark:bg-gray-900/30 p-2 rounded-md">
                  Which can also be written as: {formula.alternate}
                </div>
              )}
              
              {derivationSteps && derivationSteps.length > 0 && (
                <>
                  <button 
                    onClick={() => setShowDerivation(!showDerivation)}
                    className="mt-6 group flex items-center gap-2 px-4 py-2 rounded-lg
                               text-blue-600 dark:text-blue-400 
                               hover:bg-blue-50 dark:hover:bg-blue-900/20
                               border border-blue-200 dark:border-blue-800
                               transition-all duration-200 w-full justify-center"
                  >
                    <span className="text-sm font-medium">
                      {showDerivation ? 'Hide step-by-step derivation' : 'See how we derived this formula'}
                    </span>
                    <span className={`
                      transform transition-transform duration-200 
                      group-hover:translate-y-0.5
                      ${showDerivation ? 'rotate-180' : ''}
                    `}>
                      <svg 
                        className="w-4 h-4" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </span>
                  </button>

                  {/* Collapsible Derivation Section */}
                  <div className={`
                    mt-4 overflow-hidden transition-all duration-300 ease-in-out
                    ${showDerivation ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}
                  `}>
                    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 space-y-4">
                      {derivationSteps.map((step, index) => (
                        <div key={index} className="space-y-2">
                          <h4 className="font-medium text-gray-900 dark:text-gray-100">
                            {index + 1}. {step.step}
                          </h4>
                          <p className="text-gray-600 dark:text-gray-400 pl-4">
                            {step.explanation}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Conversion Steps Section */}
      <div className="relative mt-12">
        {/* Toned down background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-50 
                        dark:from-gray-900/30 dark:to-gray-800/30 
                        rounded-xl blur-sm"></div>
        
        <div className={`relative p-8 rounded-xl shadow-sm transition-all duration-300
          ${darkMode 
            ? 'bg-gradient-to-br from-gray-900/90 via-gray-900/95 to-gray-900/90 border border-gray-700/50' 
            : 'bg-gradient-to-br from-white/95 via-white/98 to-white/95 border border-gray-200/50'}`}>
          
          {/* Enhanced Header with Animation */}
          <div className="flex items-center space-x-4 mb-8">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-blue-600/20 
                             rounded-full blur-sm opacity-75 group-hover:opacity-100 transition duration-300"></div>
              <div className="relative flex-shrink-0 w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-900/30
                             flex items-center justify-center transform hover:scale-105 transition-transform duration-300
                             border border-blue-200/50 dark:border-blue-700/50">
                <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Step-by-Step Solution
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Follow along as we solve this conversion
              </p>
            </div>
          </div>

          {/* Example/Conversion Box */}
          <div className="mb-8">
            <div className="relative p-5 rounded-xl border group
                         bg-white dark:bg-gray-800/50
                         border-gray-200/50 dark:border-gray-700/50">
              <div className="space-y-3">
                {/* First line with badge and text */}
                <div className="flex items-center gap-3">
                  {(!result || !inputValue || inputValue === '') && (
                    <>
                      <span className="text-sm px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 
                                    text-amber-700 dark:text-amber-300 font-medium">
                        Example
                      </span>
                      <span className="text-sm italic text-gray-600 dark:text-gray-400">
                        Let's convert:
                      </span>
                    </>
                  )}
                  {result && inputValue && inputValue !== '' && (
                    <span className="text-sm px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/30 
                                  text-green-700 dark:text-green-300 font-medium">
                      Your conversion
                    </span>
                  )}
                </div>

                {/* Second line with conversion */}
                <div className="text-center font-mono text-xl">
                  <span className={`font-semibold ${(result && inputValue && inputValue !== '') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                    {(!result || !inputValue || inputValue === '') ? "25" : inputValue}
                    {fromUnit === 'Celsius' ? '°C' : fromUnit === 'Fahrenheit' ? '°F' : fromUnit === 'Kelvin' ? 'K' : ' ' + fromUnit}
                  </span>
                  <span className="mx-3 text-gray-400">→</span>
                  <span className="text-gray-700 dark:text-gray-300">
                    {toUnit === 'Celsius' ? '°C' : toUnit === 'Fahrenheit' ? '°F' : toUnit === 'Kelvin' ? 'K' : ' ' + toUnit}
                  </span>
                </div>
              </div>
            </div>
          </div>
          

          {/* Steps Container */}
          <div className="space-y-8">
            {/* Step 1: Formula */}
            {formula && (
              <div className="transform transition-all duration-300 hover:scale-[1.01]">
                <div className="relative p-6 rounded-xl bg-gradient-to-br from-blue-50/90 to-indigo-50/90 
                             dark:from-blue-900/20 dark:to-indigo-900/20 
                             border border-blue-100/50 dark:border-blue-800/30 group">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-500/5 
                               opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                  
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 
                                 flex items-center justify-center transform group-hover:rotate-6 transition-transform duration-300">
                      <span className="text-blue-600 dark:text-blue-400 font-bold text-lg">1</span>
                    </div>
                    <h4 className="font-medium text-blue-800 dark:text-blue-200">Starting Formula</h4>
                  </div>

                  <div className="text-2xl font-mono text-center bg-white/80 dark:bg-black/30 
                               p-5 rounded-lg shadow-inner border border-blue-200/50 dark:border-blue-700/30
                               transform transition-all duration-300 group-hover:shadow-lg">
                    {typeof formula === 'object' ? formula.main : formula}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Value Substitution */}
            {conversionSteps && conversionSteps[0] && (
              <div className="transform transition-all duration-300 hover:scale-[1.01]">
                <div className="relative p-6 rounded-xl bg-gradient-to-br from-gray-50/90 to-gray-50/90 
                               dark:from-gray-900/20 dark:to-gray-900/20 
                               border border-gray-200/50 dark:border-gray-700/30 group">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-purple-500/5 
                               opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                  
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 
                                   flex items-center justify-center transform group-hover:rotate-6 transition-transform duration-300">
                      <span className="text-gray-600 dark:text-gray-400 font-bold text-lg">2</span>
                    </div>
                    <h4 className="font-medium text-gray-800 dark:text-gray-200">Value Substitution</h4>
                  </div>

                  <div className="space-y-4">
                    <div className="text-gray-700 dark:text-gray-300 text-sm font-medium pl-2">
                      Let's substitute our value:
                    </div>
                    <div className="text-2xl font-mono text-center bg-white/80 dark:bg-black/30 
                                   p-5 rounded-lg shadow-sm border border-gray-200/50 dark:border-gray-700/30
                                   transform transition-all duration-300">
                      <span dangerouslySetInnerHTML={{
                        __html: conversionSteps[0].replace(
                          new RegExp(`(${inputValue})`, 'g'),
                          '<span class="text-blue-600 dark:text-blue-400">$1</span>'
                        )
                      }} />
                    </div>
                    {conversionSteps[1] && (
                      <div className="text-2xl font-mono text-center bg-white/80 dark:bg-black/30 
                                     p-5 rounded-lg shadow-sm border border-gray-200/50 dark:border-gray-700/30
                                     transform transition-all duration-300">
                        <span dangerouslySetInnerHTML={{
                          __html: conversionSteps[1].replace(
                            new RegExp(`(${inputValue})`, 'g'),
                            '<span class="text-blue-600 dark:text-blue-400">$1</span>'
                          )
                        }} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Calculation Process */}
            {conversionSteps && conversionSteps[2] && (
              <div className="transform transition-all duration-300 hover:scale-[1.01]">
                <div className="relative p-6 rounded-xl bg-gradient-to-br from-gray-50/90 to-gray-50/90 
                               dark:from-gray-900/20 dark:to-gray-900/20 
                               border border-gray-200/50 dark:border-gray-700/30 group">
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-500/5 to-gray-500/5 
                               opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                  
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 
                                   flex items-center justify-center transform group-hover:rotate-6 transition-transform duration-300">
                      <span className="text-gray-600 dark:text-gray-400 font-bold text-lg">3</span>
                    </div>
                    <div className="space-y-1">
                      <h4 className="font-medium text-gray-800 dark:text-gray-200">Calculation Process</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Solving step by step</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="text-2xl font-mono bg-white/80 dark:bg-black/30 
                                 rounded-lg shadow-sm border border-gray-200/50 dark:border-gray-700/30
                                 overflow-hidden">
                      <div className="p-4 border-b border-gray-200/50 dark:border-gray-700/30
                                   text-gray-700 dark:text-gray-300 whitespace-pre-wrap text-center
                                   [text-wrap:balance]">
                        <span dangerouslySetInnerHTML={{
                          __html: conversionSteps[2].split('\n')[0].replace(
                            new RegExp(`(${inputValue})`, 'g'),
                            '<span class="text-blue-600 dark:text-blue-400">$1</span>'
                          )
                        }} />
                      </div>
                      <div className="p-4 bg-gray-50/50 dark:bg-gray-900/20 
                                   text-gray-800 dark:text-gray-200 font-bold text-center">
                        {conversionSteps[2].split('\n')[1]}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Final Result */}
            {conversionSteps && conversionSteps[3] && (
              <div className="transform transition-all duration-300 hover:scale-[1.01]">
                <div className="relative p-6 rounded-xl bg-gradient-to-br from-blue-50/90 to-cyan-50/90 
                             dark:from-blue-900/20 dark:to-cyan-900/20 
                             border border-blue-100/50 dark:border-blue-800/30 group">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-cyan-500/5 
                               opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                  
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 
                                 flex items-center justify-center transform group-hover:rotate-6 transition-transform duration-300
                                 shadow-lg shadow-blue-500/20">
                      <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                              d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="space-y-1">
                      <h4 className="font-medium text-blue-800 dark:text-blue-200">Final Result</h4>
                      <p className="text-sm text-blue-600 dark:text-blue-400">Your converted value</p>
                    </div>
                  </div>

                  <div className="relative group/result">
                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 via-cyan-500/20 to-blue-500/20 
                                 rounded-xl blur-sm opacity-75 group-hover/result:opacity-100 transition duration-300"></div>
                    <div className="relative text-2xl font-mono text-center bg-white/90 dark:bg-black/30 
                                 p-6 rounded-lg shadow-lg border border-blue-200/50 dark:border-blue-700/30">
                      <span className="bg-gradient-to-r from-blue-600 to-cyan-600 
                                   bg-clip-text text-transparent font-bold">
                        {conversionSteps[3]}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Real-world Example */}
      {realWorldExample && (
        <div className={`relative p-8 rounded-xl shadow-lg transition-all duration-300
          ${darkMode 
            ? 'bg-gradient-to-br from-gray-800/90 via-gray-800/70 to-gray-800/90 border border-gray-700/50' 
            : 'bg-gradient-to-br from-white/90 via-white/70 to-white/90 border border-gray-200/50'}`}>
          <h3 className="text-lg font-medium mb-4">Real-world Example</h3>
          <p className="text-gray-600 dark:text-gray-400">
            {realWorldExample}
          </p>
        </div>
      )}

      {/* Fun Facts Section - Enhanced */}
      {funFacts && (
        <div className="relative mt-12">
          {/* Background decoration */}
          <div className="absolute inset-0 bg-gradient-to-br from-violet-500/10 via-purple-500/10 to-fuchsia-500/10 
                          rounded-xl blur-xl transform scale-105"></div>
          
          <div className={`relative p-8 rounded-xl shadow-lg transition-all duration-300
            ${darkMode 
              ? 'bg-gradient-to-br from-gray-800/90 via-gray-800/70 to-gray-800/90 border border-gray-700/50' 
              : 'bg-gradient-to-br from-white/90 via-white/70 to-white/90 border border-gray-200/50'}`}>
            
            {/* Fun Facts header with icon */}
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 
                             flex items-center justify-center shadow-lg shadow-purple-500/20">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold bg-gradient-to-r from-violet-600 to-purple-600 
                             bg-clip-text text-transparent">
                Did You Know?
              </h3>
            </div>

            {/* Fun Facts list */}
            <ul className="space-y-4">
              {funFacts.map((fact, index) => (
                <li key={index} className="flex items-start gap-3 group">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30 
                                 flex items-center justify-center mt-0.5
                                 group-hover:bg-purple-200 dark:group-hover:bg-purple-900/50 
                                 transition-colors duration-300">
                    <span className="text-purple-600 dark:text-purple-400 text-sm font-medium">
                      {index + 1}
                    </span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed
                               group-hover:text-gray-900 dark:group-hover:text-gray-100 
                               transition-colors duration-300">
                    {fact}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedSimpleConversionExplanation;
